import _ from 'underscore';
import { useCallback, useState } from 'react';

import { LocationTarget } from '@bingads-webui-react/bing-maps';
import {
  ENTITY_ACTION_TYPES,
  targetOptions,
  LocationTargetTypes,
  getLocationsToSave,
  fromLocationCriterionToBingMapTarget,
  fromBingMapTargetToLocationTargetEDM
} from '@bingads-webui-campaign-react/location-targeting';

const isDefaultLocationSelected = (locationTargets, defaultLocation) =>
  locationTargets.length === 1 && locationTargets[0].location.Id === defaultLocation.location.Id;

export const useTargetingLocationsAdapter = ({
  campaignId,
}) => {
  const [internalDefaultLocation, setInternalDefaultLocation] = useState(null);
  const [internalLocationTargets, setInternalLocationTargets] = useState([]);

  const mapToViewModel = useCallback((criterions, defaultLocation) => {
    const initialTargets = fromLocationCriterionToBingMapTarget(criterions);
    const defaultTarget = defaultLocation && LocationTarget.CreateIncludedLocationTarget(defaultLocation);

    const initialLocationTargets = _.filter(
      initialTargets,
      target => target.targetingType === LocationTargetTypes.Included
    );

    setInternalLocationTargets(initialLocationTargets);
    setInternalDefaultLocation(defaultTarget);

    const selectedLocationOption = _.isEmpty(initialLocationTargets) || (defaultTarget && isDefaultLocationSelected(initialLocationTargets, defaultTarget))
      ? targetOptions.quick
      : targetOptions.specific;

    const positiveLocationTargets = _.filter(initialLocationTargets, target => !defaultTarget || (target.location.Id !== defaultTarget.location.Id));

    return {
      selectedLocationOption,
      positiveLocationTargets,
    };
  }, []);

  const mapToBulkModel = useCallback(({
    selectedLocationOption,
    positiveLocationTargets: newLocationTargets,
  }) => {
    let {
      locationsToAdd,
      locationsToDelete,
    } = getLocationsToSave(internalLocationTargets, newLocationTargets);

    if (selectedLocationOption === targetOptions.quick || newLocationTargets.length === 0) {
      locationsToAdd = _.every(internalLocationTargets, target => target.location.Id !== internalDefaultLocation.location.Id)
        ? [internalDefaultLocation]
        : [];
      locationsToDelete = _.filter(internalLocationTargets, target => target.location.Id !== internalDefaultLocation.location.Id);
    }

    if (selectedLocationOption === targetOptions.specific && newLocationTargets.length > 0 &&
      _.includes(internalLocationTargets, target => target.location.Id === internalDefaultLocation.location.Id)) {
      locationsToDelete.push(internalDefaultLocation);
    }

    locationsToAdd = fromBingMapTargetToLocationTargetEDM(
      locationsToAdd,
      campaignId,
      ENTITY_ACTION_TYPES.Add
    );

    locationsToDelete = fromBingMapTargetToLocationTargetEDM(
      locationsToDelete,
      campaignId,
      ENTITY_ACTION_TYPES.Delete
    );

    locationsToDelete = _.map(
      locationsToDelete,
      deletedValue => _.extend(deletedValue, { Status: 'Deleted' })
    );

    return _.union(locationsToAdd, locationsToDelete);
  }, [campaignId, internalLocationTargets, internalDefaultLocation]);

  return {
    mapToViewModel,
    mapToBulkModel,
  };
};
